import { type Component, For, type JSX, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Drawer from '~/components/Drawer'
import type { ChipColorType } from '~/types/Chip'

export interface FilterChipType {
  label: string
  color?: ChipColorType
  icon?: string
  onRemove: () => void
}

interface TableFiltersProps {
  /** Título del drawer de filtros. */
  title: string
  /** Abierto o cerrado (lo controla la página con su propia señal). */
  isOpen: boolean
  onClose: () => void
  /** Cuántos filtros hay activos (contador del botón de limpiar). */
  activeCount: number
  /** Controles de filtro que van dentro del drawer. */
  children: JSX.Element
  /** Chips de filtros activos: se ven sin abrir el drawer y se quitan al vuelo. */
  chips?: FilterChipType[]
  /** Limpia todos los filtros (botón rojo dentro del drawer). */
  onClear?: () => void
}

/**
 * Drawer lateral de filtros + chips de activos, con el mismo aspecto en todas
 * las tablas del panel. El botón que lo abre es `FiltersButton` (para colocarlo
 * en el headerActions de la tabla).
 *
 * Nota: el Table también acepta `filterChips` (dentro de la tarjeta, sobre las
 * filas). Las páginas eligen dónde mostrar sus chips: los nuevos usan
 * `filterChips` del Table; las existentes pueden seguir usando `chips` aquí.
 */
const TableFilters: Component<TableFiltersProps> = props => {
  return (
    <>
      <Show when={(props.chips?.length ?? 0) > 0}>
        <div class="flex flex-wrap items-center gap-2 mb-4">
          <For each={props.chips ?? []}>
            {chip => (
              <Chip size="sm" color={chip.color ?? 'default'} variant="soft" removable onRemove={chip.onRemove} icon={chip.icon}>
                {chip.label}
              </Chip>
            )}
          </For>
        </div>
      </Show>

      <Drawer isOpen={props.isOpen} onClose={props.onClose} position="right" title={props.title} size="sm">
        <div class="flex flex-col p-6 space-y-6 h-full">
          {props.children}

          <Show when={props.onClear && props.activeCount > 0}>
            <Button variant="text" color="danger" onClick={props.onClear} prependIcon="material-symbols-light:filter-alt-off">
              Limpiar filtros ({props.activeCount})
            </Button>
          </Show>

          <div class="flex gap-2 mt-auto pt-2 border-t border-outline/30">
            <Button variant="soft" color="default" class="flex-1" onClick={props.onClose}>
              Cancelar
            </Button>
            <Button class="flex-1" onClick={props.onClose}>
              Ver resultados
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

/** Botón "Filtros" estándar con badge de contador, para el headerActions de la tabla. */
export function FiltersButton(props: { activeCount: number; onClick: () => void }) {
  return (
    <Show
      when={props.activeCount > 0}
      fallback={
        <Button variant="soft" color="default" onClick={props.onClick} prependIcon="material-symbols-light:tune">
          Filtros
        </Button>
      }
    >
      <Badge count={props.activeCount} color="primary">
        <Button variant="soft" color="primary" onClick={props.onClick} prependIcon="material-symbols-light:tune">
          Filtros
        </Button>
      </Badge>
    </Show>
  )
}

export default TableFilters
